import { AppBreadcrumb, AppCard, AppSpace } from '@/components/atoms';
import SiteAdminNotFoundTemplate from '@/components/templates/SiteAdminNotFoundTemplate';
import { useClinicOptionsQuery } from '@/hooks/admin/useClinicOptionsQuery';
import {
  parseClinicLocationId,
  useClinicLocationQuery,
  useUpdateClinicLocationMutation,
} from '@/hooks/admin/useClinicLocationCreate';
import { useHealthCenterRegistTypes } from '@/hooks/admin/useHealthCenterRegistTypes';
import useAppToast from '@/hooks/useAppToast';
import { SERVICE_TYPE_BOTH } from '@/constants/serviceType';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import { isAxiosError } from 'axios';
import { Spin } from 'antd';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { ClinicLocationInitialData } from './CreateClinicLocationForm';
import { CreateClinicLocationForm } from './CreateClinicLocationForm';
import type { ClinicLocationFormData } from './form-schema';
import { AuditInfo } from '@/components/molecules';

type ApiError = {
  response?: {
    data?: {
      message?: string;
      errors?: Record<string, string[]>;
    };
  };
};

export default function EditClinicLocationPage() {
  const toast = useAppToast();
  const navigate = useNavigate();
  const { site, id } = useParams<{ site: string; id: string }>();
  const { currentSite } = useSiteAdminAuth();

  const siteType = site ?? currentSite ?? 'medix';
  const locationId = id ?? null;
  const locationIdNum = parseClinicLocationId(locationId);

  const locationQuery = useClinicLocationQuery(siteType, locationId);
  const updateMutation = useUpdateClinicLocationMutation();
  const [apiErrors, setApiErrors] = useState<Record<string, string>>({});

  const { data: clinicOptions = [] } = useClinicOptionsQuery(siteType, {
    enabled: !!siteType && !!locationQuery.data,
  });
  const clinics = clinicOptions;

  const locationData = locationQuery.data?.data;
  const { data: healthCenterRegistTypesData } = useHealthCenterRegistTypes(siteType);
  const healthCenterRegistTypes = healthCenterRegistTypesData ?? [];

  /** Spec: display_id = {clinic_number}_{serial_number} for breadcrumb/title */
  const displayId = useMemo(() => {
    if (!locationData) return locationId ?? '';
    const d = locationData as {
      clinic_number?: string;
      serial_number?: number;
      display_id?: string;
    };
    return d.display_id ?? `${d.clinic_number ?? ''}_${d.serial_number ?? locationId ?? ''}`;
  }, [locationData, locationId]);

  const initialData: ClinicLocationInitialData | null = useMemo(() => {
    if (!locationData) return null;
    const apiData = locationData as { has_visits_a2?: boolean; has_visits_r2?: boolean; has_massager_a2?: boolean; has_massager_r2?: boolean } & typeof locationData;
    const st = locationData.service_type ?? SERVICE_TYPE_BOTH;
    return {
      id: locationData.id,
      clinic_id: locationData.clinic_id,
      clinic_number: locationData.clinic_number,
      serial_number: locationData.serial_number,
      service_type: st as ClinicLocationFormData['service_type'],
      service: locationData.service ?? null,
      has_visits_a2: apiData.has_visits_a2 ?? false,
      has_visits_r2: apiData.has_visits_r2 ?? false,
      has_massager_a2: apiData.has_massager_a2 ?? false,
      has_massager_r2: apiData.has_massager_r2 ?? false,
      location_name: locationData.location_name ?? '',
      location_name_kana: locationData.location_name_kana ?? '',
      available_from: locationData.available_from ?? '',
      available_to: locationData.available_to ?? '',
      health_center_regist_type_id: locationData.health_center_regist_type_id,
      postal_code: locationData.postal_code ?? '',
      address1: locationData.address1 ?? '',
      address2: locationData.address2 ?? '',
      address3: locationData.address3 ?? '',
      telephone: locationData.telephone ?? '',
      fax: locationData.fax ?? '',
      rezept_address: locationData.rezept_address ?? '',
      base_name: locationData.base_name ?? '',
      is_compnay: locationData.is_compnay ?? null,
      founder_name: locationData.founder_name ?? '',
      founder_name_kana: locationData.founder_name_kana ?? '',
      founder_postal_code: locationData.founder_postal_code ?? '',
      founder_address1: locationData.founder_address1 ?? '',
      founder_address2: locationData.founder_address2 ?? '',
      founder_address3: locationData.founder_address3 ?? '',
      founder_date_of_birth: locationData.founder_date_of_birth ?? '',
      founder_telephone: locationData.founder_telephone ?? '',
    };
  }, [locationData]);

  const breadcrumbItems = useMemo(
    () => [
      { title: '拠点', href: `/${siteType}/admin/clinic_locations` },
      { title: `編集:${displayId}` },
    ],
    [siteType, displayId]
  );

  const handleSubmit = async (data: ClinicLocationFormData) => {
    const numId = locationIdNum;
    if (numId == null) return;
    setApiErrors({});
    try {
      await updateMutation.mutateAsync({
        siteType,
        id: numId,
        payload: {
          service_type: data.service_type,
          location_name: data.location_name,
          location_name_kana: data.location_name_kana ?? '',
          available_from: data.available_from,
          available_to: data.available_to || undefined,
          health_center_regist_type_id: data.health_center_regist_type_id,
          postal_code: data.postal_code,
          address1: data.address1,
          address2: data.address2,
          address3: data.address3,
          telephone: data.telephone,
          fax: data.fax || '',
          rezept_address: data.rezept_address,
          base_name: data.base_name,
          is_compnay: data.is_compnay ?? null,
          founder_name: data.founder_name,
          founder_name_kana: data.founder_name_kana,
          founder_postal_code: data.founder_postal_code || undefined,
          founder_address1: data.founder_address1,
          founder_address2: data.founder_address2,
          founder_address3: data.founder_address3,
          founder_date_of_birth: data.founder_date_of_birth?.trim() || '',
          founder_telephone: data.founder_telephone,
        },
      });

      toast.success('更新しました。');
      navigate(`/${siteType}/admin/clinic_locations/${numId}`);
    } catch (err: unknown) {
      const e = err as ApiError;
      const data = e.response?.data;
      const errors = data?.errors;
      if (errors && typeof errors === 'object') {
        // service_type error → toast only, not inline
        const serviceTypeError = Array.isArray(errors.service_type) ? errors.service_type[0] : null;
        if (serviceTypeError) {
          toast.error(serviceTypeError);
          return;
        }
        const fieldErrors: Record<string, string> = {};
        for (const [k, v] of Object.entries(errors)) {
          const msg = Array.isArray(v) ? (v[0] as string) : String(v);
          if (msg) fieldErrors[k] = msg;
        }
        if (Object.keys(fieldErrors).length > 0) {
          // clinic_id (治療院 soft delete 等): toast のみ。インラインは出さない
          const { clinic_id: _clinicIdOmit, ...restFieldErrors } = fieldErrors;
          setApiErrors(restFieldErrors);
          toast.error('エラーが発生しました。');
          return;
        }
      }
      setApiErrors({});
      toast.error('エラーが発生しました。');
    }
  };

  const handleCancel = () => {
    const clinicId = initialData?.clinic_id;
    if (clinicId != null) {
      navigate(`/${siteType}/admin/clinics/${clinicId}`);
    } else {
      navigate(`/${siteType}/admin/clinics`);
    }
  };

  if (locationQuery.isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center p-6">
        <Spin size="large" />
      </div>
    );
  }

  if (locationQuery.isError) {
    const status = isAxiosError(locationQuery.error) ? locationQuery.error.response?.status : null;
    if (status === 404) return <SiteAdminNotFoundTemplate />;
    return (
      <div className="flex min-h-[200px] items-center justify-center p-6 text-red-600">
        {locationQuery.error?.message ?? '拠点の取得に失敗しました。'}
      </div>
    );
  }

  if (!initialData) {
    return <SiteAdminNotFoundTemplate />;
  }

  return (
    <div className="p-6">
      <AppSpace orientation="vertical" className="w-full" size="large">
        <AppBreadcrumb items={breadcrumbItems} />

        <h1 className="text-xl font-semibold">拠点編集:{displayId}</h1>

        <AppCard className="w-full">
          <CreateClinicLocationForm
            key={`edit-${initialData.id}`}
            clinics={clinics}
            healthCenterRegistTypes={healthCenterRegistTypes}
            preselectedClinicId={initialData.clinic_id}
            clinicIdFromUrl={initialData.clinic_id}
            clinicNumberDisplay={
              apiErrors.clinic_id && clinics.length > 0
                ? clinics[0].clinic_number
                : initialData.clinic_number ?? ''
            }
            initialData={initialData}
            editSerialNumber={initialData.serial_number}
            editDisplayId={displayId}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={updateMutation.isPending}
            siteType={siteType}
            apiErrors={apiErrors}
          />
          <AuditInfo
            createdBy={locationData?.created_by}
            updatedBy={locationData?.updated_by}
            updatedAt={locationData?.updated_at}
          />
        </AppCard>
      </AppSpace>
    </div>
  );
}
